import {useCallback, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {Dialog, DialogContent} from '@tinct/web-components';
import {cn} from '@tinct/core-utils';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {
  AudienceImportContextRef,
  CrmSegment,
  ImportResponse,
  ReadAudience,
  ReadCompanyRef,
} from '@tinct/core-api-models';
import {useGetAudienceById} from '@tinct/core-api';
import {
  useAudienceImportJobAlert,
  useCreateAudienceActions,
  useCreateAudienceModal,
} from './hooks';
import {AddCompaniesStep, CreateAudienceStep, ImportingStep, MappingStep, SuccessStep} from './steps';

type CreateAudienceModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (audience: ReadAudience, importResult?: ImportResponse) => void;
  /** Pass an existing audience to skip the create step and go directly to add companies */
  existingAudience?: ReadAudience | null;
  /** Base URL for campaign links shown in the success step */
  campaignBaseUrl?: string;
  /** Where the job-alert widget link should send the user once the import
   *  job completes. */
  completedUrl: string;
};

const IMPORT_POLL_INTERVAL_MS = 2000;

const audienceContextFor = (audience: ReadAudience | null): AudienceImportContextRef | null => {
  if (!audience) return null;
  if (audience.id) return {id: audience.id};
  if (audience.name) return {name: audience.name};
  return null;
};

export const CreateAudienceModal = ({
  open,
  onOpenChange,
  onSuccess,
  existingAudience,
  campaignBaseUrl,
  completedUrl,
}: CreateAudienceModalProps) => {
  const {
    step,
    audience,
    setAudience,
    fileBase64,
    analysisResult,
    mapping,
    setMapping,
    importResult,
    setImportResult,
    goToAddCompanies,
    goToMapping,
    goToImporting,
    goToSuccess,
    goBack,
    reset,
  } = useCreateAudienceModal({ initialAudience: existingAudience });

  const { analyzeAction, importAction } = useCreateAudienceActions();

  const navigate = useNavigate();
  const workspaceUrl = useCurrentWorkspaceUrlPrefix();
  const {start: startJobAlert} = useAudienceImportJobAlert({
    jobId: importResult?.jobId,
    completedUrl
  });

  // Register the import job in the global job-alert store as soon as we know
  // its id. Idempotent — re-registering merges with the existing entry.
  useEffect(() => {
    if (importResult?.jobId) startJobAlert(importResult.jobId);
  }, [importResult?.jobId, startJobAlert]);

  // Refresh audience metadata (name, campaigns, isReady) while the import is
  // in flight. Job progression itself is tracked by ImportingStep via the
  // jobId returned by the import response.
  const monitoredAudienceId = audience?.id ?? null;
  const isMonitoringImport = step === 'importing' && !!monitoredAudienceId;

  const {data: monitoredAudience} = useGetAudienceById(
    {
      id: monitoredAudienceId ?? '',
      fields: {
        id: null,
        name: null,
        isReady: null,
        campaigns: null,
      },
    },
    {
      enabled: isMonitoringImport,
      refetchInterval: (query) => {
        const a = query.state.data;
        if (a?.isReady) return false;
        return IMPORT_POLL_INTERVAL_MS;
      },
    },
  );

  // Step transition is driven by the job's status (ImportingStep#onJobCompleted).
  // `displayAudience` (below) is used to surface the latest monitored audience
  // data without mutating local state.

  // While the audience exists but hasn't reported readiness, lock the modal closed.
  const isLockedClosed = !!audience?.id && audience.isReady === false;

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen && isLockedClosed) return;
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  // Step 1 (create) — no mutation. Just stash the chosen name on a "pending"
  // audience shape so subsequent steps can carry it through.
  const handleCreateAudience = useCallback(
    (name: string) => {
      const pending: ReadAudience = {
        id: null,
        name,
        leads: null,
        organization: null,
        statistics: null,
        campaigns: null,
        createdAt: null,
        updatedAt: null,
        isReady: null,
        lastImportJob: null,
        ipMatchRate: null,
      };
      goToAddCompanies(pending);
    },
    [goToAddCompanies],
  );

  const audienceContext = useMemo(() => audienceContextFor(audience), [audience]);

  // CSV file selection — analyze with the pending audience context
  const handleCsvFileSelect = useCallback(
    async (file: File | null) => {
      if (!file || !audienceContext) return;
      const result = await analyzeAction.analyzeCsv({file, audienceContext});
      if (result) goToMapping(result.analysis, result.base64);
    },
    [audienceContext, analyzeAction, goToMapping],
  );

  // Capture the server-returned audience so we can start monitoring it.
  const handleImportResponse = useCallback(
    (response: ImportResponse | undefined) => {
      if (!response) {
        goBack();
        return;
      }
      // Store the import response immediately so the importing step can read
      // `jobId` to poll job progression. The transition to `success` is then
      // driven by the job's status (see ImportingStep#onJobCompleted).
      setImportResult(response);
      // If the server created/upserted an audience, swap our pending placeholder
      // for the real one so downstream views show the right name/id.
      const returned = response.audience;
      if (returned?.id) {
        setAudience({
          id: returned.id,
          name: returned.name ?? audience?.name ?? null,
          leads: null,
          organization: null,
          statistics: null,
          campaigns: null,
          createdAt: null,
          updatedAt: null,
          isReady: null,
          lastImportJob: null,
          ipMatchRate: null,
        });
      }
    },
    [audience, setAudience, setImportResult, goBack],
  );

  const handleImportCsv = useCallback(async () => {
    if (!audienceContext || !fileBase64) return;
    goToImporting();
    const response = await importAction.importCsv({
      audienceContext,
      fileBase64,
      mapping,
    });
    handleImportResponse(response);
  }, [audienceContext, fileBase64, mapping, importAction, goToImporting, handleImportResponse]);

  const handleImportCompanies = useCallback(
    async (companies: ReadCompanyRef[]) => {
      if (!audienceContext) return;
      goToImporting();
      const response = await importAction.importCompanies({
        audienceContext,
        companies,
      });
      handleImportResponse(response);
    },
    [audienceContext, importAction, goToImporting, handleImportResponse],
  );

  const handleImportCrmSegment = useCallback(
    async (segment: CrmSegment, integrationId: string) => {
      if (!audienceContext) return;
      goToImporting();
      const response = await importAction.importCrmSegment({
        audienceContext,
        integrationId,
        segment,
      });
      handleImportResponse(response);
    },
    [audienceContext, importAction, goToImporting, handleImportResponse],
  );

  const handleSkip = () => {
    if (!audience) return;
    reset();
    onOpenChange(false);
    onSuccess?.(audience);
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  // "Run in background" — close the modal (bypassing the readiness lock) and
  // send the user to the dashboard. The job stays tracked via the global
  // job-alert store, which shows progress in the top alert bar / floating widget.
  const handleRunInBackground = () => {
    reset();
    onOpenChange(false);
    navigate(`${workspaceUrl}/dashboard`);
  };

  const handleDone = () => {
    if (!audience || !importResult) return;
    reset();
    onOpenChange(false);
    onSuccess?.(audience, importResult);
  };

  // The audience we present to downstream steps: prefer the live poll data
  // once available (latest name, campaigns, etc.), otherwise our local copy.
  const displayAudience = monitoredAudience ?? audience;

  const isOverlayStep = step === 'importing' || step === 'success';
  const isSmallStep = step === 'create' || isOverlayStep;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={!isLockedClosed}
        onPointerDownOutside={(e) => {
          if (isLockedClosed) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isLockedClosed) e.preventDefault();
        }}
        className={cn({
          'sm:max-w-[425px] rounded-3xl p-8': isSmallStep,
          'sm:max-w-[740px] rounded-2xl p-8': !isSmallStep,
          'p-0 border-0 bg-transparent shadow-none': isOverlayStep,
        })}
      >
        {step === 'create' && (
          <CreateAudienceStep
            onCancel={handleCancel}
            onCreateAudience={handleCreateAudience}
          />
        )}

        {step === 'add-companies' && displayAudience && (
          <AddCompaniesStep
            audience={displayAudience}
            isAnalyzing={analyzeAction.isAnalyzing}
            onCsvFileSelect={handleCsvFileSelect}
            onImportCompanies={handleImportCompanies}
            onImportCrmSegment={handleImportCrmSegment}
            onBack={existingAudience ? handleCancel : goBack}
            onSkip={handleSkip}
          />
        )}

        {step === 'mapping' && analysisResult && (
          <MappingStep
            analysisResult={analysisResult}
            mapping={mapping}
            onMappingChange={setMapping}
            onBack={goBack}
            onContinue={handleImportCsv}
          />
        )}

        {step === 'importing' && (
          <ImportingStep
            audienceName={displayAudience?.name ?? null}
            jobId={importResult?.jobId ?? null}
            onJobCompleted={() => {
              if (importResult) goToSuccess(importResult);
            }}
            onRunInBackground={handleRunInBackground}
          />
        )}

        {step === 'success' && importResult && (
          <SuccessStep
            audienceName={displayAudience?.name || 'Audience'}
            importResult={importResult}
            onDone={handleDone}
            campaigns={displayAudience?.campaigns ?? undefined}
            campaignBaseUrl={campaignBaseUrl}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
